import { Component, OnInit, OnDestroy, ViewChildren, ElementRef, QueryList, inject, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IonicModule, Platform } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { images } from '../../_shared/data/image';
import { AuthService } from '../auth.service';
import { ToastService } from '../../_core/services/toast.service';
import { LoaderService } from '../../_core/services/loader.service';
import { NavController } from '@ionic/angular';
import { Router } from '@angular/router';
import { finalize, take } from 'rxjs/operators';
import { NgOtpInputModule } from 'ng-otp-input';
import { firstValueFrom, Observable } from 'rxjs';
import { ModalController } from '@ionic/angular';
import { IS_NEW_UI_FLOW } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-verification-otp',
  templateUrl: './verification-otp.component.html',
  styleUrls: ['./verification-otp.component.scss'],
  standalone: true,
  imports: [IonicModule, ReactiveFormsModule, CommonModule, NgOtpInputModule]
})
export class VerificationOtpComponent implements OnInit, OnDestroy {
  @Input() state: any;
  @Input() isManualVerification: boolean = false;
  private platform = inject(Platform);
  public logoSquare = images.logoSquare;
  public isLoading: boolean = false;
  public isResending: boolean = false;
  public phone: string = '';
  public country_code: string = '';
  public email: string = '';
  public password: string = '';
  public userId: string = '';
  public isEmailRequired: boolean = false;
  public timeLeft: number = 30;
  public canResend: boolean = false;
  private countdownInterval: any;
  public screenHeight: number = this.platform.height();
  public otpValue: string = '';
  route: string = IS_NEW_UI_FLOW ? '/auth' : '/login'; // Default route
  public showPassword: boolean = false;
  public manualVerificationForm!: FormGroup;
  public verificationType: 'email' | 'phone' | null = null;
  public isProfileUpdate: boolean = false;
  public otpLength: number = 6;

  private normalizeEmail(value: string | null | undefined): string {
    return value?.trim().toLowerCase() ?? '';
  }

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private toastService: ToastService,
    private loaderService: LoaderService,
    private navCtrl: NavController,
    private router: Router,
    public modalCtrl: ModalController,
    private translateService: TranslateService
  ) {
  }

  ngOnInit() {
    // Get data from navigation state or modal componentProps
    let stateData: any = {};

    // Check if state was passed via componentProps (modal)
    if (this.state) {
      stateData = this.state;
    } else {
      // Check if state was passed via router navigation
      const navigation = this.router.getCurrentNavigation();
      if (navigation?.extras?.state) {
        stateData = navigation.extras.state as any;
      }
    }

    // Extract data from state
    this.phone = stateData.phone || '';
    this.country_code = stateData.country_code || '';
    this.email = this.normalizeEmail(stateData.email);
    this.password = stateData.password || '';
    this.userId = stateData.userId || '';
    this.isEmailRequired = stateData.isEmailRequired || false;
    this.isManualVerification = stateData.isManualVerification || this.isManualVerification;

    this.buildManualVerificationForm();
    // Start countdown timer
    this.isProfileUpdate = stateData.isProfileUpdate || false;
    this.verificationType = stateData.verificationType || null;

    if (this.verificationType === 'phone') {
      this.otpLength = 4;
    } else {
      this.otpLength = 6;
    }

    // Initially show "Resend the code" - countdown will start after clicking
    this.canResend = true;
  }

  ngOnDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  buildManualVerificationForm() {
    this.manualVerificationForm = this.fb.group({
      email: [this.email || '', [Validators.required, Validators.email]],
      code: ['', [Validators.required, Validators.minLength(4)]],
    });
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  startCountdown() {
    this.timeLeft = 30;
    this.canResend = false;
    this.countdownInterval = setInterval(() => {
      this.timeLeft--;
      if (this.timeLeft <= 0) {
        this.canResend = true;
        clearInterval(this.countdownInterval);
      }
    }, 1000);
  }

  private getErrorMessage(err: any): string {
    return (
      err?.error?.message ||
      err?.message ||
      (err?.status === 400 ? this.translateService.instant('Invalid verification code.') : null) ||
      this.translateService.instant(err || err?.error?.message || err?.message || 'Something went wrong. Please try again.'));
  }

  private buildRequest$(isPasswordChangeFlow: boolean, otp?: string): Observable<any> {
    if (isPasswordChangeFlow) {
      // adjust payload as needed
      const userData = { /* ... */ };
      return this.authService.verifyEmail(userData);
    }

    const code = this.otpValue?.trim() ?? otp?.trim();
    
    if (this.isProfileUpdate) {
      if (this.verificationType === 'email') {
        return this.authService.VerifyEmailOTP(
          this.email, this.phone, code
        );
      } else if (this.verificationType === 'phone') {
        return this.authService.VerifyPhoneOTP(this.email, this.phone, code);
      }
    }

    if (this.isManualVerification) {
      const verificationData = {
        email: this.email,
        code,
        isManualVerification: true
      };
      return this.authService.verifyManualCognitoUser(verificationData);
    }

    const verificationData = {
      email: this.email,
      code,                    // no duplicate keys
      password: this.password, // include only if your API requires it
    } as any;

    return this.authService.verifyCognitoUser(verificationData);
  }

  async verify(opts?: { isPasswordChange?: boolean; otp?: string }) {
    const isPasswordChangeFlow = !!opts?.isPasswordChange;
    const otp = opts?.otp?.trim();

    if (this.isManualVerification) {
      if (this.manualVerificationForm.invalid) {
        await this.toastService.presentDangerToast('Please fill all required fields correctly.');
        return;
      }
      const formVal = this.manualVerificationForm.value;
      this.email = this.normalizeEmail(formVal.email);
      this.manualVerificationForm.patchValue({ email: this.email }, { emitEvent: false });
      this.otpValue = formVal.code.trim();
    }

    if (!this.otpValue && !otp && !isPasswordChangeFlow) {
      await this.toastService.presentDangerToast(
        this.translateService.instant(`Please enter the complete 6-digit code.`)
      );
      await this.toastService.presentDangerToast(
        this.translateService.instant(`Please enter the complete ${this.otpLength}-digit code.`)
      );
      return;
    }

    if (this.otpValue && this.otpValue.length !== this.otpLength) {
      await this.toastService.presentDangerToast(
        this.translateService.instant(`Please enter a valid ${this.otpLength}-digit code.`)
      );
      return;
    }


    this.isLoading = true;
    try {
      this.loaderService.present?.();

      const request$ = this.buildRequest$(isPasswordChangeFlow, otp);
      const response = await firstValueFrom(request$);

      if (this.isProfileUpdate) {
        await this.toastService.presentToast(
          this.translateService.instant('Verification successful!'),
          'success'
        );
        this.modalCtrl.dismiss({ verified: true });
        return;
      }

      if (this.isManualVerification) {
        await this.handleManualVerificationResponse(response);
        return;
      }

      await this.handleLoginResponse(response);
    } catch (err) {
      await this.toastService.presentDangerToast(this.getErrorMessage(err));
    } finally {
      this.isLoading = false;
      this.loaderService.dismiss?.();
    }
  }

  async handleManualVerificationResponse(response: any) {
    if (response?.status === 'VERIFIED' || response?.message?.includes('verified')) {
      await this.toastService.presentToast(
        this.translateService.instant('Email verified successfully!'),
        'success'
      );
      
      if (this.state?.isLoginModal) {
        this.modalCtrl.dismiss({ verified: true });
      } else {
        this.navCtrl.navigateRoot('/auth/signin');
      }
    } else {
      await this.toastService.presentDangerToast(
        this.translateService.instant('Verification failed. Please try again.')
      );
    }
  }

  async handleLoginResponse(response: any) {
    const {
      accessToken,
      token,
      refreshToken,
      cognitoUsername
    } = response?.data || {};

    if (accessToken || token) {
      if (token) localStorage.setItem('token', token);
      if (accessToken) localStorage.setItem('accessToken', accessToken);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      if (cognitoUsername) localStorage.setItem('cognitoUsername', cognitoUsername);

      await this.authService.getCognitoUser();
      // await this.toastService.presentToast(this.translateService.instant('Login successful!'), 'success');
      if (!this.state?.isLoginModal) {
        // Check for returnTo URL first (for /join/{team} redirects)
        const returnTo = localStorage.getItem('returnTo');
        if (returnTo) {
          localStorage.removeItem('returnTo');
          this.navCtrl.navigateRoot(returnTo);
        } else {
          this.navCtrl.navigateRoot('/main-menu');
        }
      } else {
        this.modalCtrl.dismiss({ success: true });
      }
    } else {
      await this.toastService.presentDangerToast(this.translateService.instant('Invalid response from server'));
    }
  }

  async resendCode() {
    if (!this.canResend) return;

    this.isResending = true;
    await this.loaderService.present();

    let resendObservable: Observable<any> | null = null;

    this.email = this.normalizeEmail(this.email);

    if (this.isProfileUpdate) {
      if (this.verificationType === 'email') {
        resendObservable = this.authService.sendEmailVerificationCode({ email: this.email, phone:this.phone });
      } else if (this.verificationType === 'phone') {
        resendObservable = this.authService.phoneVerification(this.email, this.phone);
      }else if(this.isManualVerification){
        const formVal = this.manualVerificationForm.value;
        this.email = this.normalizeEmail(formVal.email);
        this.manualVerificationForm.patchValue({ email: this.email }, { emitEvent: false });
        resendObservable = this.authService.resendCognitoCode(this.email);
      }
    } else {
      // Check if manual verification is enabled and read email from form
      if (this.isManualVerification) {
        const formVal = this.manualVerificationForm.value;
        this.email = this.normalizeEmail(formVal.email);
        this.manualVerificationForm.patchValue({ email: this.email }, { emitEvent: false });
      }
      resendObservable = this.authService.resendCognitoCode(this.email);
    }

    if (!resendObservable) {
      this.isResending = false;
      this.loaderService.dismiss();
      return;
    }

    resendObservable
      .pipe(
        take(1),
        finalize(() => {
          this.isResending = false;
          this.loaderService.dismiss();
        })
      )
      .subscribe({
        next: async (response) => {
          await this.toastService.presentToast(this.translateService.instant('Verification code resent successfully!'));
          // Start countdown after resending - this will show the timer
          this.startCountdown();
        },
        error: async (error) => {
          console.error('Resend error:', error);
          let errorMessage = this.translateService.instant('Failed to resend code. Please try again.');

          if (error?.error?.message) {
            errorMessage = error.error.message;
          }

          await this.toastService.presentDangerToast(errorMessage);
        }
      });
  }

  getFormattedPhone(): string {
    return `${this.country_code}${this.phone}`;
  }

  getFormattedEmail(): string {
    return this.email || this.getFormattedPhone();
  }

  getDisplayContact(): string {
    if (this.verificationType === 'phone') {
      return this.phone;
    }
    return this.email;
  }

  getVerificationMessage(): string {
    if (this.verificationType === 'phone') {
      return 'We\'ve just sent you a verification code to your phone number above.';
    }
    return 'We\'ve just sent you a verification code to your email above.';
  }

  getTimeString(): string {
    const minutes = Math.floor(this.timeLeft / 60);
    const seconds = this.timeLeft % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }


  onOtpChange(otp: string) {
    this.otpValue = otp.trim();
  }

  gotoFeedback() {
    this.router.navigate(['/feedback']);
  }

}
